import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserBrands } from "../../hooks/useBrands.ts";
import { useCampaigns } from "../../hooks/useCampaigns.ts";
import { GetNotifications, GetNotificationsUnread } from "../../services/Common.ts";
import { FaBuilding, FaBullhorn, FaChartBar, FaUpload, FaPlus, FaRocket, FaClock, FaCheckCircle, FaSync, FaBell, FaInfoCircle, FaExclamationCircle, FaMapMarkerAlt, FaCalendar, FaMoneyBillWave, FaUsers } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

interface Notification {
  id: number;
  title: string;
  description: string;
  type: 'info' | 'warning' | 'success' | 'error';
  read: boolean;
  created_at: string;
  action_url?: string;
}

interface Brand {
  id: string;
  title: string;
  description: string;
  logo: string;
  location: string;
  district: string;
  state: string;
  pincode: string;
  added_by: number;
  created_at: string;
}

interface Campaign {
  id: string;
  title: string;
  overview: string;
  banner: string;
  type: string;
  deadline: string;
  campaign_budget: string;
  budget_left: string;
  individual_budget: string;
  is_live: boolean;
  platforms: string[];
  brand_details: Brand;
  created_at: string;
}

const LandingPage: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const navigation = useNavigate();
  const [stats, setStats] = useState({
    brands: 0,
    campaigns: 0
  });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notificationsLoading, setNotificationsLoading] = useState(true);

  const reawdata = localStorage.getItem('user');
  let cred = JSON.parse(reawdata || '{}');
  let token = cred?.access;
  let id = cred?.user?.id;

  const { data: brandsData } = useUserBrands(id);
  const { data: campaignsData } = useCampaigns(id);

  // Brands and Campaigns fetched using React Query

  const fetchNotifications = async () => {
    try {
      setNotificationsLoading(true);
      const response = await GetNotifications(token, id);
      if (response && response.data) {
        // Get only the latest 3 notifications for the dashboard
        const latestNotifications = response.data.slice(0, 3);
        setNotifications(latestNotifications);

        // Calculate unread count
        const unread = response.data.filter((n: Notification) => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await GetNotificationsUnread(token);
      if (response && response.data) {
        setUnreadCount(response.data.count || response.data.length || 0);
      }
    } catch (err) {
      console.error("Error fetching unread count:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
      setStats((prev) => ({ ...prev, brands: brandsData.length }));
    }
    if (campaignsData) {
      setCampaigns(campaignsData);
      setStats((prev) => ({ ...prev, campaigns: campaignsData.length }));
      setLoading(false);
    }
  }, [brandsData, campaignsData]);

  // Data for charts
  const barChartData = [
    { name: 'Brands', value: stats?.brands || 0 },
    { name: 'Campaigns', value: stats?.campaigns || 0 }
  ];

  const pieChartData = [
    { name: 'Brands', value: stats?.brands || 0 },
    { name: 'Campaigns', value: stats?.campaigns || 0 }
  ];

  const COLORS = [currentTheme.primary, currentTheme.secondary];
  const BAR_COLORS = [currentTheme.primary, currentTheme.secondary];

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInHours * 60);
        return `${diffInMinutes}m ago`;
      } else if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `${hours}h ago`;
      } else {
        const days = Math.floor(diffInHours / 24);
        return `${days}d ago`;
      }
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    const iconClass = "text-base";
    switch (type) {
      case 'success': return <FaCheckCircle className={`${iconClass} text-green-500`} />;
      case 'warning': return <FaExclamationCircle className={`${iconClass} text-yellow-500`} />;
      case 'error': return <FaExclamationCircle className={`${iconClass} text-red-500`} />;
      default: return <FaInfoCircle className={`${iconClass} text-blue-500`} />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: currentTheme.background }}>
        <div className="text-center">
          <FaSync className="animate-spin text-4xl mx-auto mb-4" style={{ color: currentTheme.primary }} />
          <p style={{ color: currentTheme.text }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: currentTheme.background }} className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: currentTheme.text }}>
            Welcome Back, {cred?.user?.profile?.organization_name}
          </h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: currentTheme.textSecondary }}>
            Manage your brands, campaigns, and verifications from one place.
          </p>

          {/* Stats Row */}
          {/* <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-bold mb-2" style={{ color: currentTheme.primary }}>{stats.brands}</p>
              <p className="text-sm uppercase tracking-wider" style={{ color: currentTheme.textSecondary }}>Brands</p>
            </div>
            <div className="w-px h-12" style={{ backgroundColor: currentTheme.border }}></div>
            <div className="text-center">
              <p className="text-3xl font-bold mb-2" style={{ color: currentTheme.secondary }}>{stats.campaigns}</p>
              <p className="text-sm uppercase tracking-wider" style={{ color: currentTheme.textSecondary }}>Campaigns</p>
            </div>
          </div> */}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div
            className="rounded-2xl p-6 transition-all duration-300 hover:transform hover:-translate-y-1"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>Total Brands</p>
                <p className="text-3xl font-bold" style={{ color: currentTheme.primary }}>{stats.brands}</p>
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                <FaBuilding size={24} style={{ color: currentTheme.primary }} />
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl p-6 transition-all duration-300 hover:transform hover:-translate-y-1"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>Active Campaigns</p>
                <p className="text-3xl font-bold" style={{ color: currentTheme.secondary }}>{stats.campaigns}</p>
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.secondary}15` }}>
                <FaBullhorn size={24} style={{ color: currentTheme.secondary }} />
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl p-6 transition-all duration-300 hover:transform hover:-translate-y-1"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>Live Campaigns</p>
                <p className="text-3xl font-bold" style={{ color: currentTheme.success }}>
                  {campaigns.filter(camp => camp.is_live).length}
                </p>
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.success}15` }}>
                <FaRocket size={24} style={{ color: currentTheme.success }} />
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl p-6 transition-all duration-300 hover:transform hover:-translate-y-1"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium mb-2" style={{ color: currentTheme.textSecondary }}>Total Budget</p>
                <p className="text-3xl font-bold" style={{ color: currentTheme.warning }}>
                  ₹{campaigns.reduce((total, camp) => total + parseFloat(camp.campaign_budget || '0'), 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.warning}15` }}>
                <FaMoneyBillWave size={24} style={{ color: currentTheme.warning }} />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Brands & Campaigns Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Brands */}
          <div
            className="rounded-2xl p-6"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center" style={{ color: currentTheme.text }}>
                <FaBuilding className="mr-3" /> Recent Brands
              </h3>
              <button
                onClick={() => navigation('/dashboard/brands')}
                className="text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: `${currentTheme.primary}15`,
                  color: currentTheme.primary
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.primary;
                  e.currentTarget.style.color = currentTheme.surface;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.primary}15`;
                  e.currentTarget.style.color = currentTheme.primary;
                }}
              >
                View All
              </button>
            </div>

            <div className="space-y-4">
              {brands.slice(0, 3).map((brand) => (
                <div
                  key={brand.id}
                  className="flex items-center space-x-4 p-4 rounded-lg transition-all duration-200 cursor-pointer hover:shadow-md"
                  style={{
                    backgroundColor: `${currentTheme.background}30`,
                    border: `1px solid ${currentTheme.border}`
                  }}
                  onClick={() => navigation('/dashboard/brands')}
                >
                  <img
                    src={brand.logo}
                    alt={brand.title}
                    className="w-12 h-12 rounded-lg object-cover border"
                    style={{ borderColor: currentTheme.border }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm mb-1 truncate" style={{ color: currentTheme.text }}>
                      {brand.title}
                    </h4>
                    <p className="text-xs flex items-center mb-1" style={{ color: currentTheme.textSecondary }}>
                      <FaMapMarkerAlt className="mr-1" size={10} />
                      {brand.location}, {brand.state}
                    </p>
                    <p className="text-xs" style={{ color: currentTheme.textMuted }}>
                      Created {formatTime(brand.created_at)}
                    </p>
                  </div>
                </div>
              ))}

              {brands.length === 0 && (
                <div className="text-center py-8" style={{ color: currentTheme.textSecondary }}>
                  <FaBuilding className="mx-auto text-3xl mb-3 opacity-50" />
                  <p className="font-medium">No brands yet</p>
                  <p className="text-sm mt-1">Create your first brand to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Campaigns */}
          <div
            className="rounded-2xl p-6"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center" style={{ color: currentTheme.text }}>
                <FaBullhorn className="mr-3" /> Recent Campaigns
              </h3>
              <button
                onClick={() => navigation('/dashboard/campaings')}
                className="text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300"
                style={{
                  backgroundColor: `${currentTheme.secondary}15`,
                  color: currentTheme.secondary
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.secondary;
                  e.currentTarget.style.color = currentTheme.surface;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.secondary}15`;
                  e.currentTarget.style.color = currentTheme.secondary;
                }}
              >
                View All
              </button>
            </div>

            <div className="space-y-4">
              {campaigns.slice(0, 3).map((campaign) => (
                <div
                  key={campaign.id}
                  className="p-4 rounded-lg transition-all duration-200 cursor-pointer hover:shadow-md"
                  style={{
                    backgroundColor: `${currentTheme.background}30`,
                    border: `1px solid ${currentTheme.border}`
                  }}
                  onClick={() => navigation('/dashboard/campaings')}
                >
                  <div className="flex items-start space-x-4">
                    <img
                      src={campaign.banner}
                      alt={campaign.title}
                      className="w-16 h-16 rounded-lg object-cover border"
                      style={{ borderColor: currentTheme.border }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-sm truncate" style={{ color: currentTheme.text }}>
                          {campaign.title}
                        </h4>
                        {campaign.is_live && (
                          <span
                            className="text-xs font-medium px-2 py-1 rounded-full ml-2"
                            style={{
                              backgroundColor: `${currentTheme.success}15`,
                              color: currentTheme.success
                            }}
                          >
                            Live
                          </span>
                        )}
                      </div>
                      <p className="text-xs mb-2 line-clamp-2" style={{ color: currentTheme.textSecondary }}>
                        {campaign.overview}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center" style={{ color: currentTheme.textMuted }}>
                          <FaCalendar className="mr-1" size={10} />
                          {formatDate(campaign.deadline)}
                        </div>
                        <div className="font-medium" style={{ color: currentTheme.primary }}>
                          ₹{parseFloat(campaign.campaign_budget).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {campaigns.length === 0 && (
                <div className="text-center py-8" style={{ color: currentTheme.textSecondary }}>
                  <FaBullhorn className="mx-auto text-3xl mb-3 opacity-50" />
                  <p className="font-medium">No campaigns yet</p>
                  <p className="text-sm mt-1">Launch your first campaign to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          // Bar Chart Card 
          <div 
            className="rounded-2xl p-6"
            style={{ 
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <h3 className="text-xl font-bold mb-6 flex items-center" style={{ color: currentTheme.text }}>
              <FaChartBar className="mr-3" /> Activity Overview
            </h3>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.border} />
                  <XAxis dataKey="name" tick={{ fill: currentTheme.textSecondary }} />
                  <YAxis tick={{ fill: currentTheme.textSecondary }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: currentTheme.surface,
                      border: `1px solid ${currentTheme.border}`,
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                  <Bar
                    dataKey="value"
                    radius={[8, 8, 0, 0]}
                    barSize={60}
                  >
                    {barChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS?.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          // Pie Chart Card 
          
          <div 
            className="rounded-2xl p-6"
            style={{ 
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
            }}
          >
            <h3 className="text-xl font-bold mb-6 flex items-center" style={{ color: currentTheme.text }}>
              <FaChartBar className="mr-3" /> Distribution
            </h3>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS?.length]}
                        stroke={currentTheme.surface}
                        strokeWidth={3}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: currentTheme.surface,
                      border: `1px solid ${currentTheme.border}`,
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div> */}

        {/* Quick Actions & Notifications */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-6 flex items-center" style={{ color: currentTheme.text }}>
              <FaRocket className="mr-3" /> Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className="rounded-2xl p-6 text-center transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer"
                style={{
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}
                onClick={() => navigation('/dashboard/documents')}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                  <FaUpload size={24} style={{ color: currentTheme.primary }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: currentTheme.text }}>Upload Documents</h3>
                <p className="text-sm mb-4" style={{ color: currentTheme.textSecondary }}>Submit and manage your verifications</p>
                <button
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-300"
                  style={{
                    backgroundColor: currentTheme.primary,
                    color: currentTheme.surface
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.primary}
                >
                  Get Started
                </button>
              </div>

              <div
                className="rounded-2xl p-6 text-center transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer"
                style={{
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}
                onClick={() => navigation('/dashboard/brands')}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${currentTheme.secondary}15` }}>
                  <FaPlus size={24} style={{ color: currentTheme.secondary }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: currentTheme.text }}>Manage Brands</h3>
                <p className="text-sm mb-4" style={{ color: currentTheme.textSecondary }}>View and add multiple brands</p>
                <button
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-300"
                  style={{
                    backgroundColor: currentTheme.secondary,
                    color: currentTheme.surface
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary}
                >
                  Manage Brands
                </button>
              </div>

              <div
                className="rounded-2xl p-6 text-center transition-all duration-300 hover:transform hover:-translate-y-2 cursor-pointer"
                style={{
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}
                onClick={() => navigation('/dashboard/campaings')}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${currentTheme.success}15` }}>
                  <FaBullhorn size={24} style={{ color: currentTheme.success }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: currentTheme.text }}>Run Campaign</h3>
                <p className="text-sm mb-4" style={{ color: currentTheme.textSecondary }}>Create and manage campaigns easily</p>
                <button
                  className="px-4 py-2 rounded-lg font-medium transition-all duration-300"
                  style={{
                    backgroundColor: currentTheme.success,
                    color: currentTheme.surface
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.success}
                >
                  Start Campaign
                </button>
              </div>
            </div>
          </div>

          {/* Notifications Section with Real API Data */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center" style={{ color: currentTheme.text }}>
                <FaBell className="mr-3" /> Notifications
              </h2>
              {unreadCount > 0 && (
                <span
                  className="text-white text-sm font-medium rounded-full px-3 py-1"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  {unreadCount} unread
                </span>
              )}
            </div>

            <div
              className="rounded-2xl p-6"
              style={{
                backgroundColor: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
              }}
            >
              {notificationsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: currentTheme.primary }}></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-8" style={{ color: currentTheme.textSecondary }}>
                  <FaBell className="mx-auto text-3xl mb-3 opacity-50" />
                  <p className="font-medium">No notifications</p>
                  <p className="text-sm mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`flex items-start space-x-3 p-4 rounded-lg transition-all duration-200 cursor-pointer ${!notification.read ? 'border-l-4' : ''
                        }`}
                      style={{
                        backgroundColor: !notification.read
                          ? `${currentTheme.primary}08`
                          : `${currentTheme.background}30`,
                        borderLeftColor: !notification.read ? currentTheme.primary : 'transparent'
                      }}
                      onClick={() => navigation('/dashboard/notifications')}
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm mb-1" style={{ color: currentTheme.text }}>
                          {notification.title}
                        </p>
                        <p className="text-xs line-clamp-2" style={{ color: currentTheme.textSecondary }}>
                          {notification.description}
                        </p>
                        <p className="text-xs mt-2 font-medium" style={{ color: currentTheme.textMuted }}>
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* View All Notifications Button */}
              <button
                className="w-full mt-6 py-3 rounded-lg font-medium transition-all duration-300 border flex items-center justify-center"
                style={{
                  backgroundColor: 'transparent',
                  color: currentTheme.primary,
                  borderColor: currentTheme.primary
                }}
                onClick={() => navigation('/dashboard/notifications')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.primary;
                  e.currentTarget.style.color = currentTheme.surface;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentTheme.primary;
                }}
              >
                <FaBell className="mr-2" />
                View All Notifications
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;